import React, { createContext, useContext, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { CMS_API_URL } from '../config/apiBase';

/* ─────────────────────────────────────────────────────────
 * Token helpers — one cart-token per brand in localStorage
 * ───────────────────────────────────────────────────────── */
const tokenKey = (brandSlug) => `${brandSlug}_cart_token`;
const getToken = (brandSlug) => localStorage.getItem(tokenKey(brandSlug));
const setToken = (brandSlug, token) => {
  if (token) localStorage.setItem(tokenKey(brandSlug), token);
};

/* ─────────────────────────────────────────────────────────
 * Normalize cart line prices: the API splits unitPrice (base) and
 * additionalPrice (attribute add-ons). The cart UI treats unitPrice as the full
 * per-unit price, so fold the add-ons in (and zero them out) to avoid showing
 * just the base price on each line and in the totals.
 * ───────────────────────────────────────────────────────── */
const foldUnitPrice = (items) => (items || []).map((it) => ({
  ...it,
  unitPrice: (Number(it.unitPrice) || 0) + (Number(it.additionalPrice) || 0),
  additionalPrice: 0,
}));

const normalizeCart = (data) => {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    ...(data.products ? { products: foldUnitPrice(data.products) } : {}),
    ...(data.packages ? { packages: foldUnitPrice(data.packages) } : {}),
  };
};

/* ─────────────────────────────────────────────────────────
 * Cart store (mutable object + subscriber pattern for useSyncExternalStore)
 * ───────────────────────────────────────────────────────── */
function createCartStore() {
  const carts = {};              // { [brandSlug]: cartData }
  const loading = {};            // { [brandSlug]: boolean }
  const fetched = {};            // { [brandSlug]: boolean }
  let listeners = new Set();

  const emptyCart = {
    token: null,
    products: [],
    packages: [],
    itemCount: 0,
    productsTotal: 0,
    packagesTotal: 0,
    subtotal: 0,
    discount: null,
    discountAmount: 0,
    total: 0,
  };

  // Stable snapshot reference — only replaced inside notify()
  let snapshot = { carts, loading };

  function notify() {
    snapshot = { carts, loading };
    listeners.forEach((l) => l());
  }

  function getSnapshot() {
    return snapshot;
  }

  function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  /* ── API helpers ── */
  async function apiFetch(brandSlug, path, options = {}) {
    const token = getToken(brandSlug);
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { 'X-Cart-Token': token } : {}),
    };
    const res = await fetch(`${CMS_API_URL}/api/cart/${brandSlug}${path}`, {
      ...options,
      headers: { ...headers, ...options.headers },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // Laravel validation errors come as { message, errors: { field: [msg] } };
      // other BE errors use { error }.
      const validationMsg = body.errors ? Object.values(body.errors).flat()[0] : null;
      const error = new Error(body.error || body.message || validationMsg || `Cart API error ${res.status}`);
      // Keep the response on the error: callers branch on machine codes the
      // API sends alongside the message (e.g. `code: 'shop_mismatch'` with
      // both shop names → the shop-switch popup instead of a toast).
      error.status = res.status;
      error.data = body;
      throw error;
    }
    return res.json();
  }

  // silent: skip notify() — used by the mutation actions, whose finally block
  // emits a single notify carrying both the new cart data and loading=false
  // (instead of two back-to-back re-renders of every subscriber).
  function applyData(brandSlug, data, { silent = false } = {}) {
    // Cart-token rotation: BE may issue a new token (school change, post-checkout fresh cart)
    const newToken =
      data?.cart?.token ?? data?.token ?? null;
    const oldToken = getToken(brandSlug);
    if (newToken && newToken !== oldToken) {
      setToken(brandSlug, newToken);
      // eslint-disable-next-line no-console
      console.log('[checkout] cart.token rotated', { brandSlug, oldToken, newToken });
    }
    // Cart shape is the response itself for cart endpoints; keep it.
    if (data && (data.products || data.packages || typeof data.itemCount === 'number')) {
      carts[brandSlug] = normalizeCart(data);
      if (!silent) notify();
    }
  }

  /* ── Public actions ── */
  async function fetchCart(brandSlug) {
    if (fetched[brandSlug] || loading[brandSlug]) return;
    if (!getToken(brandSlug)) {
      carts[brandSlug] = { ...emptyCart };
      notify();
      return;
    }
    loading[brandSlug] = true;
    notify();
    try {
      const data = await apiFetch(brandSlug, '');
      carts[brandSlug] = normalizeCart(data);
    } catch {
      carts[brandSlug] = { ...emptyCart };
    } finally {
      loading[brandSlug] = false;
      fetched[brandSlug] = true;
      notify();
    }
  }

  async function addItem(brandSlug, payload) {
    loading[brandSlug] = true;
    notify();
    try {
      const data = await apiFetch(brandSlug, '/items', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      applyData(brandSlug, data, { silent: true });
      fetched[brandSlug] = true;
      return data;
    } finally {
      loading[brandSlug] = false;
      notify();
    }
  }

  async function updateItem(brandSlug, itemId, quantity) {
    loading[brandSlug] = true;
    notify();
    try {
      const data = await apiFetch(brandSlug, `/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify({ quantity }),
      });
      applyData(brandSlug, data, { silent: true });
    } finally {
      loading[brandSlug] = false;
      notify();
    }
  }

  async function removeItem(brandSlug, itemId) {
    loading[brandSlug] = true;
    notify();
    try {
      const data = await apiFetch(brandSlug, `/items/${itemId}`, {
        method: 'DELETE',
      });
      applyData(brandSlug, data, { silent: true });
    } finally {
      loading[brandSlug] = false;
      notify();
    }
  }

  async function applyDiscount(brandSlug, code) {
    const data = await apiFetch(brandSlug, '/discount', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    applyData(brandSlug, data);
    return data;
  }

  async function removeDiscount(brandSlug) {
    const data = await apiFetch(brandSlug, '/discount', {
      method: 'DELETE',
    });
    applyData(brandSlug, data);
  }

  async function setSchool(brandSlug, payload) {
    const data = await apiFetch(brandSlug, '/school', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    applyData(brandSlug, data);
    fetched[brandSlug] = true;
    return data;
  }

  async function clearCart(brandSlug) {
    const items = [
      ...(carts[brandSlug]?.products ?? []),
      ...(carts[brandSlug]?.packages ?? []),
    ];
    const hasDiscount = Boolean(carts[brandSlug]?.discount);
    if (!items.length && !hasDiscount) return;

    loading[brandSlug] = true;
    notify();
    try {
      // Delete all lines concurrently — each targets a distinct item, so order
      // doesn't matter; the authoritative cart state comes from the final GET.
      await Promise.allSettled(
        items.map((it) => apiFetch(brandSlug, `/items/${it.id}`, { method: 'DELETE' })),
      );
      if (hasDiscount) {
        try {
          await apiFetch(brandSlug, '/discount', { method: 'DELETE' });
        } catch { /* noop */ }
      }
      const data = await apiFetch(brandSlug, '');
      applyData(brandSlug, data, { silent: true });
    } catch { /* keep the last known state */ }
    finally {
      loading[brandSlug] = false;
      notify();
    }
  }

  async function checkout(brandSlug, payload) {
    const data = await apiFetch(brandSlug, '/checkout', {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    });
    // eslint-disable-next-line no-console
    console.log('[checkout] checkout response', { brandSlug, paymentMode: data?.payment?.mode, orderNumber: data?.orderNumber });

    // Token rotation (e.g. invoice path issues a fresh cart)
    const newToken = data?.cart?.token ?? null;
    if (newToken) setToken(brandSlug, newToken);

    // Reset local cart only if payment is fully completed (Invoice path).
    if (data?.payment?.mode === 'completed') {
      carts[brandSlug] = {
        token: newToken ?? carts[brandSlug]?.token ?? null,
        products: [],
        packages: [],
        itemCount: 0,
        productsTotal: 0,
        packagesTotal: 0,
        subtotal: 0,
        discount: null,
        discountAmount: 0,
        total: 0,
      };
      notify();
    }
    return data;
  }

  async function confirmPayment(brandSlug, orderNumber, status, providerRef) {
    const res = await fetch(`${CMS_API_URL}/api/payments/${brandSlug}/${encodeURIComponent(orderNumber)}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ status: status ?? null, providerRef: providerRef ?? null }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const validationMsg = body.errors ? Object.values(body.errors).flat()[0] : null;
      throw new Error(body.error || body.message || validationMsg || `Payment confirm error ${res.status}`);
    }
    const data = await res.json();
    // eslint-disable-next-line no-console
    console.log('[checkout] payment.confirm', { brandSlug, orderNumber, paymentStatus: data?.paymentStatus });

    const newToken = data?.cart?.token ?? null;
    if (newToken) {
      setToken(brandSlug, newToken);
      carts[brandSlug] = {
        token: newToken,
        products: [],
        packages: [],
        itemCount: 0,
        productsTotal: 0,
        packagesTotal: 0,
        subtotal: 0,
        discount: null,
        discountAmount: 0,
        total: 0,
      };
      notify();
    }
    return data;
  }

  return {
    subscribe,
    getSnapshot,
    fetchCart,
    addItem,
    updateItem,
    removeItem,
    applyDiscount,
    removeDiscount,
    setSchool,
    clearCart,
    checkout,
    confirmPayment,
  };
}

/* ─────────────────────────────────────────────────────────
 * Singleton store
 * ───────────────────────────────────────────────────────── */
const store = createCartStore();

/* ─────────────────────────────────────────────────────────
 * React context (provides the store instance)
 * ───────────────────────────────────────────────────────── */
const CartContext = createContext(store);

export function CartProvider({ children }) {
  return <CartContext.Provider value={store}>{children}</CartContext.Provider>;
}

/* ─────────────────────────────────────────────────────────
 * useCart(brandSlug) — the main hook for components
 * ───────────────────────────────────────────────────────── */
export function useCart(brandSlug) {
  const s = useContext(CartContext);

  const { carts, loading: loadingMap } = useSyncExternalStore(
    s.subscribe,
    s.getSnapshot,
  );

  // Fetch cart on first use (only once per brand). This must run in an effect:
  // fetchCart may notify useSyncExternalStore subscribers, which cannot happen
  // while another Header instance is still rendering.
  const initRef = useRef({});
  useEffect(() => {
    if (!brandSlug || initRef.current[brandSlug]) return;
    initRef.current[brandSlug] = true;
    s.fetchCart(brandSlug);
  }, [s, brandSlug]);

  const cart = carts[brandSlug] || {
    token: null,
    products: [],
    packages: [],
    itemCount: 0,
    productsTotal: 0,
    packagesTotal: 0,
    subtotal: 0,
    discount: null,
    discountAmount: 0,
    total: 0,
  };

  const isLoading = !!loadingMap[brandSlug];

  const addItem = useCallback(
    (payload) => s.addItem(brandSlug, payload),
    [s, brandSlug],
  );

  const updateItem = useCallback(
    (itemId, qty) => s.updateItem(brandSlug, itemId, qty),
    [s, brandSlug],
  );

  const removeItem = useCallback(
    (itemId) => s.removeItem(brandSlug, itemId),
    [s, brandSlug],
  );

  const applyDiscount = useCallback(
    (code) => s.applyDiscount(brandSlug, code),
    [s, brandSlug],
  );

  const removeDiscount = useCallback(
    () => s.removeDiscount(brandSlug),
    [s, brandSlug],
  );

  const checkout = useCallback(
    (payload) => s.checkout(brandSlug, payload),
    [s, brandSlug],
  );

  const confirmPayment = useCallback(
    (orderNumber, status, providerRef) => s.confirmPayment(brandSlug, orderNumber, status, providerRef),
    [s, brandSlug],
  );

  const setSchool = useCallback(
    (payload) => s.setSchool(brandSlug, payload),
    [s, brandSlug],
  );

  const clearCart = useCallback(
    () => s.clearCart(brandSlug),
    [s, brandSlug],
  );

  return {
    ...cart,
    loading: isLoading,
    addItem,
    updateItem,
    removeItem,
    applyDiscount,
    removeDiscount,
    setSchool,
    clearCart,
    checkout,
    confirmPayment,
  };
}
